import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

interface Bookmark {
  id: string;
  content_type: string;
  content_id: string;
  title: string;
  description?: string;
  metadata?: any;
  created_at: string;
}

export function useBookmarks() {
  const [bookmarks, setBookmarks] = useState<Bookmark[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      loadBookmarks();
    } else {
      setBookmarks([]);
      setLoading(false);
    }
  }, [user]);

  const loadBookmarks = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('bookmarks')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBookmarks(data || []);
    } catch (error) {
      console.error('Error loading bookmarks:', error);
      toast.error('Failed to load bookmarks');
    } finally {
      setLoading(false);
    }
  };

  const addBookmark = async (
    contentType: string,
    contentId: string,
    title: string,
    description?: string,
    metadata?: any
  ) => {
    if (!user) {
      toast.error('Please sign in to save bookmarks');
      return;
    }

    try {
      const { data, error } = await supabase
        .from('bookmarks')
        .insert({
          user_id: user.id,
          content_type: contentType,
          content_id: contentId,
          title,
          description,
          metadata
        })
        .select()
        .single();

      if (error) throw error;
      
      setBookmarks([data, ...bookmarks]);
      toast.success('Saved to bookmarks');
    } catch (error) {
      console.error('Error adding bookmark:', error);
      toast.error('Failed to save bookmark');
    }
  };

  const removeBookmark = async (bookmarkId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('bookmarks')
        .delete()
        .eq('id', bookmarkId)
        .eq('user_id', user.id);

      if (error) throw error;
      
      setBookmarks(bookmarks.filter(b => b.id !== bookmarkId));
      toast.success('Removed from bookmarks');
    } catch (error) {
      console.error('Error removing bookmark:', error);
      toast.error('Failed to remove bookmark');
    }
  };

  const isBookmarked = (contentType: string, contentId: string): boolean => {
    return bookmarks.some(
      b => b.content_type === contentType && b.content_id === contentId
    );
  };

  const getBookmarkId = (contentType: string, contentId: string): string | undefined => {
    return bookmarks.find(
      b => b.content_type === contentType && b.content_id === contentId
    )?.id;
  };

  return {
    bookmarks,
    loading,
    addBookmark,
    removeBookmark,
    isBookmarked,
    getBookmarkId,
    refreshBookmarks: loadBookmarks
  };
}
